import { ArrowRight, Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { openBookingModal } from "./BookingModal";

type SiteHeaderProps = {
  scrolled: boolean;
  onNavigate?: (href: string) => void;
};

const AGENTS_URL =
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:3021"
    : "https://agents.anter.ai";

// "Why Anter" is the main commercial-intent page and was footer-only, which
// left it near-orphaned — header nav is the strongest internal link on the site.
const NAV_LINKS = [
  { label: "Why Anter", href: "/why-anter", external: false },
  { label: "Docs", href: "/docs", external: false },
  { label: "Chat SDK", href: "/chat-sdk", external: false },
  { label: "Resources", href: "/resources", external: false },
  { label: "Design Partners", href: "/pricing", external: false },
];

function navigate(href: string) {
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export default function SiteHeader({ scrolled, onNavigate }: SiteHeaderProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);
  const [authed, setAuthed] = useState(() => {
    try {
      return localStorage.getItem("anter:authenticated") === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onAuthed = () => setAuthed(true);
    const onNeedsAuth = () => setAuthed(false);
    window.addEventListener("anter:authenticated", onAuthed);
    window.addEventListener("anter:needs-auth", onNeedsAuth);
    return () => {
      window.removeEventListener("anter:authenticated", onAuthed);
      window.removeEventListener("anter:needs-auth", onNeedsAuth);
    };
  }, []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  // Keep the accessibility tree well-formed: the drawer stays mounted while
  // closed (so its slide-out transition can play), so `inert` on the <main>
  // is what actually removes the rest of the page from focus/AT while the
  // dialog is open — aria-modal="true" alone doesn't do this for every
  // consumer of the tree. Also move focus in/out of the dialog like any
  // modal should.
  useEffect(() => {
    const toInert = document.querySelectorAll("main, footer, [data-chat-provider]");
    toInert.forEach((el) => el.toggleAttribute("inert", drawerOpen));

    if (drawerOpen) {
      closeButtonRef.current?.focus();
    } else if (wasOpenRef.current) {
      openButtonRef.current?.focus();
    }
    wasOpenRef.current = drawerOpen;

    return () => {
      toInert.forEach((el) => el.removeAttribute("inert"));
    };
  }, [drawerOpen]);

  // Close on Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  // Close on navigation
  useEffect(() => {
    const onNav = () => setDrawerOpen(false);
    window.addEventListener("popstate", onNav);
    return () => window.removeEventListener("popstate", onNav);
  }, []);

  function handleNavClick(href: string, external: boolean) {
    if (external) {
      window.open(href, "_blank", "noreferrer");
    } else if (onNavigate) {
      onNavigate(href);
    } else {
      navigate(href);
    }
    setDrawerOpen(false);
  }

  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header
        inert={drawerOpen}
        className={`fixed inset-x-0 top-0 z-40 w-full border-b transition-colors duration-200 ${scrolled ? "border-border" : "border-transparent"}`}
        style={{
          background: "color-mix(in oklab, var(--color-bg) 75%, transparent)",
          backdropFilter: "saturate(180%) blur(14px)",
          WebkitBackdropFilter: "saturate(180%) blur(14px)",
        }}
      >
        <div className="mx-auto flex h-12 w-full max-w-7xl items-center justify-between px-4 sm:px-5 md:px-8">
          {/* Logo */}
          <a
            href="/"
            aria-label="Anter — home"
            className="inline-flex items-center gap-2 rounded-md text-text transition hover:text-primary-text"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("/", false);
            }}
          >
            <img src="/favicon.svg" alt="Anter logo" aria-hidden="true" className="h-5 w-5" />
            <span className="font-display text-lg font-medium tracking-[-0.02em]">anter</span>
          </a>

          {/* Desktop nav */}
          <nav aria-label="Primary" className="hidden items-center gap-1 sm:flex">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.external ? "_blank" : undefined}
                rel={link.external ? "noreferrer" : undefined}
                className="rounded-md px-3 py-1.5 text-sm font-medium text-text-muted transition hover:text-text"
              >
                {link.label}
              </a>
            ))}
            {authed ? (
              <a
                href={AGENTS_URL}
                target="_blank"
                rel="noreferrer"
                className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-primary-border bg-primary-dim px-4 py-1.5 text-sm font-semibold text-primary-text transition hover:border-primary hover:bg-bg-hover"
              >
                Dashboard
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            ) : (
              <>
                <a
                  href={`${AGENTS_URL}/login`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-text-muted transition hover:text-text"
                >
                  Sign in
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
                <a
                  href={AGENTS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-1 inline-flex items-center gap-1.5 rounded-full border border-primary-border bg-primary-dim px-4 py-1.5 text-sm font-semibold text-primary-text transition hover:border-primary hover:bg-bg-hover"
                >
                  Start Free
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </>
            )}
          </nav>

          {/* Mobile — hamburger only */}
          <button
            ref={openButtonRef}
            type="button"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            aria-controls="site-menu-drawer"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-border bg-bg-secondary text-text transition hover:bg-bg-hover sm:hidden"
          >
            <Menu className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Scrim */}
      {drawerOpen && (
        <div
          aria-hidden
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Slide-over drawer */}
      <aside
        id="site-menu-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        inert={!drawerOpen}
        className={`fixed inset-y-0 right-0 z-50 flex w-72 flex-col border-l border-border bg-bg shadow-2xl transition-transform duration-300 ease-in-out ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Drawer header */}
        <div className="flex h-12 items-center justify-between border-b border-border px-5">
          <a
            href="/"
            aria-label="Anter — home"
            className="inline-flex items-center gap-2 text-text transition hover:text-primary-text"
            onClick={() => setDrawerOpen(false)}
          >
            <img src="/favicon.svg" alt="Anter logo" aria-hidden="true" className="h-5 w-5" />
            <span className="font-display text-lg font-medium tracking-[-0.02em]">anter</span>
          </a>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="flex h-11 w-11 items-center justify-center rounded-md border border-border text-text-faint transition hover:bg-bg-hover hover:text-text"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {/* Drawer links */}
        <nav aria-label="Primary" className="flex flex-col gap-1 p-4">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target={link.external ? "_blank" : undefined}
              rel={link.external ? "noreferrer" : undefined}
              onClick={() => setDrawerOpen(false)}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-medium text-text-muted transition hover:bg-bg-hover hover:text-text"
            >
              {link.label}
              <ArrowRight className="h-3.5 w-3.5 text-text-faint" />
            </a>
          ))}
          {!authed && (
            <a
              href={`${AGENTS_URL}/login`}
              target="_blank"
              rel="noreferrer"
              onClick={() => setDrawerOpen(false)}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-text-muted transition hover:bg-bg-hover hover:text-text"
            >
              Sign in
              <ArrowRight className="h-3.5 w-3.5 text-text-faint" aria-hidden="true" />
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          )}
        </nav>

        {/* Drawer footer CTA */}
        <div className="mt-auto border-t border-border p-4 flex flex-col gap-2">
          <a
            href={authed ? AGENTS_URL : `${AGENTS_URL}/signup`}
            target="_blank"
            rel="noreferrer"
            onClick={() => setDrawerOpen(false)}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-primary bg-primary py-2.5 text-sm font-bold text-primary-text-strong transition hover:opacity-90"
          >
            {authed ? "Open Dashboard" : "Start Building Free"}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          <button
            type="button"
            onClick={() => {
              setDrawerOpen(false);
              openBookingModal();
            }}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-border bg-bg-secondary py-2.5 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover cursor-pointer"
          >
            Book Architecture Review
          </button>
        </div>
      </aside>
    </>
  );
}
